import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import DashboardLayout from '../../components/layout/DashboardLayout';
import apiClient from '../../utils/api';

const MenuSettingsContainer = styled.div`
  max-width: 1200px;
  width: 100%;
`;

const Title = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 2rem;
`;

const InfoBox = styled.div`
  background: #e7f5ff;
  border: 1px solid #339af0;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const InfoText = styled.div`
  font-size: 0.9375rem;
  color: #1971c2;
  line-height: 1.6;
  font-weight: 600;
`;

const MenuList = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  
  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 12px;
  }
`;

const MenuCard = styled.div`
  padding: 1.5rem;
  border: 2px solid #dee2e6;
  border-radius: 12px;
  margin-bottom: 1rem;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #000000;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const MenuHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const MenuInfo = styled.div`
  flex: 1;
`;

const MenuName = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const MenuIcon = styled.span`
  font-size: 1.25rem;
`;

const MenuPath = styled.div`
  font-size: 0.8125rem;
  color: #6c757d;
  font-family: monospace;
  background: #f8f9fa;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  display: inline-block;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 60px;
  height: 34px;
  flex-shrink: 0;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
  
  &:checked + span {
    background-color: #000000;
  }
  
  &:checked + span:before {
    transform: translateX(26px);
  }
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .3s;
  border-radius: 34px;
  
  &:before {
    position: absolute;
    content: "";
    height: 26px;
    width: 26px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    transition: .3s;
    border-radius: 50%;
  }
`;

const StatusText = styled.span<{ active?: boolean }>`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${props => props.active ? '#0f5132' : '#6c757d'};
  margin-left: 0.75rem;
  
  @media (max-width: 768px) {
    margin-left: 0;
    margin-top: 0.5rem;
    display: block;
  }
`;

interface MenuConfig {
  id: string;
  name: string;
  icon: string;
  path: string;
  enabled: boolean;
}

const AdminMenuSettings: React.FC = () => {
  const [menuSettings, setMenuSettings] = useState<MenuConfig[]>([]);
  const [loading, setLoading] = useState(true);

  // 메뉴 설정 조회
  const fetchMenus = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/menus');
      if (response.data.success) {
        setMenuSettings(response.data.data);
      }
    } catch (error) {
      console.error('메뉴 설정 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMenus();
  }, []);

  const handleToggle = async (menuId: string) => {
    try {
      const response = await apiClient.put(`/menus/${menuId}/toggle`);
      if (response.data.success) {
        // 목록 새로고침
        await fetchMenus();
        window.alert(response.data.message);
      }
    } catch (error: any) {
      console.error('메뉴 상태 변경 실패:', error);
      window.alert(error.response?.data?.detail || '메뉴 상태 변경에 실패했습니다');
    }
  };

  return (
    <DashboardLayout title="메뉴 관리">
      <MenuSettingsContainer>
        <Title>회원 메뉴 관리</Title>
        
        <InfoBox>
          <InfoText>
            💡 토글을 활성화하면 회원들에게 해당 메뉴가 표시됩니다.
            <br />
            💡 토글을 비활성화하면 회원들에게 해당 메뉴가 숨겨집니다.
            <br />
            💡 관리자는 모든 메뉴에 항상 접근할 수 있습니다.
          </InfoText>
        </InfoBox>
        
        <MenuList>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#6c757d' }}>로딩 중...</div>
          ) : menuSettings.length > 0 ? (
            menuSettings.map(menu => (
            <MenuCard key={menu.id}>
              <MenuHeader>
                <MenuInfo>
                  <MenuName>
                    <MenuIcon>{menu.icon}</MenuIcon>
                    {menu.name}
                  </MenuName>
                  <MenuPath>{menu.path}</MenuPath>
                </MenuInfo>
                
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <ToggleSwitch>
                    <ToggleInput
                      type="checkbox"
                      checked={menu.enabled}
                      onChange={() => handleToggle(menu.id)}
                    />
                    <ToggleSlider />
                  </ToggleSwitch>
                  <StatusText active={menu.enabled}>
                    {menu.enabled ? '활성화' : '비활성화'}
                  </StatusText>
                </div>
              </MenuHeader>
            </MenuCard>
            ))
          ) : (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#6c757d' }}>메뉴가 없습니다.</div>
          )}
        </MenuList>
      </MenuSettingsContainer>
    </DashboardLayout>
  );
};

export default AdminMenuSettings;

